/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { Merchant } from "../../../models";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService } from "../../../services";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

@Component({
  selector: "app-fraud-and-risk",
  templateUrl: "./fraud-and-risk.component.html"
})
export class FraudAndRiskComponent extends MerchantDetailsParentComponent implements OnInit {
  @Input("merchant") merchant!: Merchant;
  fraudRiskForm!: FormGroup;
  editable = false;
  actionInProgress = false;
  buttonName = "Update Fraud / Risk Variables";

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.fraudRiskForm = this.fb.group({
      duplicateDollarTxCount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.duplicateDollarTxCount : "",
        [Validators.min(0), Validators.max(9999)]
      ],
      duplicateDollarTxAmount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.duplicateDollarTxAmount : "",
        [Validators.min(0), Validators.max(99999)]
      ],
      excessiveVolumeIncreasePercent1vs90: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreasePercent1vs90 : "",
        [Validators.min(100)]
      ],
      excessiveVolumeIncreaseAmount1vs90: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreaseAmount1vs90 : "",
        [Validators.min(0), Validators.max(999999999)]
      ],
      excessiveVolumeIncreasePercent1vs30: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreasePercent1vs30 : "",
        [Validators.min(100)]
      ],
      excessiveVolumeIncreaseAmount1vs30: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreaseAmount1vs30 : "",
        [Validators.min(0), Validators.max(999999999)]
      ],
      excessiveVolumeIncreasePercent1vs7: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreasePercent1vs7 : "",
        [Validators.min(100)]
      ],
      excessiveVolumeIncreaseAmount1vs7: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreaseAmount1vs7 : "",
        [Validators.min(0), Validators.max(999999999)]
      ],
      excessiveVolumeIncreasePercent7vs90: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreasePercent7vs90 : "",
        [Validators.min(100)]
      ],
      excessiveVolumeIncreaseAmount7vs90: [
        this.merchant.fraudVars ? this.merchant.fraudVars.excessiveVolumeIncreaseAmount7vs90 : "",
        [Validators.min(0), Validators.max(999999999)]
      ],
      dailyUnmatchedCreditsCount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.dailyUnmatchedCreditsCount : "",
        [Validators.min(0), Validators.max(9999)]
      ],
      dailyUnmatchedCreditsAmount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.dailyUnmatchedCreditsAmount : "",
        [Validators.min(0), Validators.max(99999)]
      ],
      sameCardTxCount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.sameCardTxCount : "",
        [Validators.min(0), Validators.max(99)]
      ],
      sameCardTxAmount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.sameCardTxAmount : "",
        [Validators.min(0), Validators.max(99999)]
      ],
      totalDailyForeignCardCount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.totalDailyForeignCardCount : "",
        [Validators.min(0), Validators.max(99)]
      ],
      totalDailyForeignCardTxAmount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.totalDailyForeignCardTxAmount : "",
        [Validators.min(0), Validators.max(99999)]
      ],
      txLimitToAlert: [
        this.merchant.fraudVars ? this.merchant.fraudVars.txLimitToAlert : "",
        [Validators.min(0), Validators.max(9999999)]
      ],
      singleForcedTxAmount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.singleForcedTxAmount : "",
        [Validators.min(0), Validators.max(99999)]
      ],
      aggregateForcedTxAmount: [
        this.merchant.fraudVars ? this.merchant.fraudVars.aggregateForcedTxAmount : "",
        [Validators.min(0), Validators.max(999999)]
      ],
      staleTxDays: [
        this.merchant.fraudVars ? this.merchant.fraudVars.staleTxDays : "",
        [Validators.min(0), Validators.max(999)]
      ]
    });
  }

  setEditable(value: boolean): void {
    this.editable = value;
    this.createForm();
  }

  updateFraudRisk() {
    if (this.fraudRiskForm.valid) {
      this.actionInProgress = true;
      this.merchantService.updateFraudsData(this.merchant, this.fraudRiskForm.value).subscribe(
        (result) => {
          if (this.merchant) {
            this.merchant.fraudVars = result;
            this.actionInProgress = false;
            this.setEditable(false);
          } else {
            this.actionInProgress = false;
            this.setEditable(true);
          }
        },
        (error) => {
          this.notificationService.error(error);
          this.actionInProgress = false;
        }
      );
    }
  }
}
